import React, { useState } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  DatePicker,
  notification,
} from "antd";
import { useTranslation } from "react-i18next";
import { useCreateTaskExecutionMutation, MachineItem } from "shared/api";
import { useGetTasksQuery } from "shared/api/task-hub";
import { handleRequestError } from "shared/helpers";
import { getThemeColor } from "./taskHubHelpers";

interface CreateTaskExecutionModalProps {
  open: boolean;
  onClose: () => void;
  allUsers: { label: string; value: number | null | undefined }[];
  machinesData: MachineItem[] | undefined;
}

export const CreateTaskExecutionModal: React.FC<
  CreateTaskExecutionModalProps
> = ({ open, onClose, allUsers, machinesData }) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const themeColor = getThemeColor();

  const [form] = Form.useForm();
  const [createTaskExecution, { isLoading }] = useCreateTaskExecutionMutation();
  const [taskType, setTaskType] = useState<"one_time" | "unscheduled">(
    "one_time"
  );

  const { data: tasksData, isFetching } = useGetTasksQuery(
    {
      offset: 0,
      limit: 500,
      expand: "machines,product_cards,files,users,companies",
    },
    { skip: !open }
  );

  const tasksOptions =
    tasksData?.results?.map((t) => ({
      label: t.name,
      value: t.id,
    })) ?? [];

  const machinesOptions = machinesData?.map((m) => ({
    label: m.name,
    value: m.id,
  }));

  const priorityOptions = [1, 2, 3, 4, 5].map((n) => ({
    label: String(n),
    value: n,
  }));

  const handleFinish = async (values: any) => {
    try {
      const payload = {
        task: taskType === "one_time" ? null : values.task,
        timestamp_expected: values.timestamp_expected
          ? values.timestamp_expected.format()
          : null,
        user: values.user || null,
        machine: values.machine || null,
        message: values.message,
        priority: values.priority,
        status: "PENDING",
      };

      await createTaskExecution(payload).unwrap();
      api.success({
        message: t("taskHub.createExecutionModal.successTitle"),
        description: t("taskHub.createExecutionModal.successDescription"),
        placement: "topRight",
      });
      onClose();
      form.resetFields();
      setTaskType("one_time");
    } catch (error) {
      const description = handleRequestError(
        error,
        t("taskHub.createExecutionModal.errorDefaultDescription")
      );
      api.error({
        message: t("taskHub.createExecutionModal.errorTitle"),
        description,
        placement: "topRight",
      });
      /* eslint-disable no-console */
      console.error("Failed to create task execution", error);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setTaskType("one_time");
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Create one-time or unscheduled task"
        open={open}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ priority: 1 }}
        >
          <Form.Item label="Task Type">
            <Select
              value={taskType}
              onChange={(val) => setTaskType(val)}
              options={[
                { label: "One time task", value: "one_time" },
                { label: "Unscheduled Task", value: "unscheduled" },
              ]}
            />
          </Form.Item>

          {taskType === "unscheduled" && (
            <Form.Item
              name="task"
              label="Task"
              rules={[{ required: true, message: "Please select a task" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={isFetching}
                options={tasksOptions}
                placeholder="Select a task"
                className="w-full"
              />
            </Form.Item>
          )}

          <Form.Item
            name="timestamp_expected"
            label="Expected Time"
            rules={[{ required: true, message: "Please select expected time" }]}
          >
            <DatePicker showTime className="w-full" />
          </Form.Item>

          <Form.Item name="user" label="User">
            <Select options={allUsers} placeholder="Select a user" allowClear />
          </Form.Item>

          <Form.Item name="machine" label="Machine">
            <Select
              options={machinesOptions}
              placeholder="Select a machine"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: "Please enter a message" }]}
          >
            <Input.TextArea placeholder="Enter message" rows={3} />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: "Please select priority" }]}
          >
            <Select
              placeholder="Select priority"
              options={priorityOptions}
              className="w-full"
            />
          </Form.Item>

          <div className="flex justify-end gap-2 mt-6">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              style={{ backgroundColor: themeColor, borderColor: themeColor }}
            >
              Create
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
